import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

import LoadingButton from "@mui/lab/LoadingButton";
import Check from "@mui/icons-material/Check";

import { useGetDataMutation } from "../../../../api/forms.api.slice";
import { showNotification } from "../../../../features/core/slices/notification.slice";
import { requireFieldsInfo } from "../../context/formSlice";
import { updateDialogActionPanel } from "../../context/dialogFormSlice";
import {
  constructSearchUrl,
  formRecordSelectionReq as getParams,
} from "../../services/form.service";
import { notifyError } from "../../../../utils/helpers";

function VerifyAndUpdateBtn(props) {
  const {
    fieldProps,
    setValue,
    getValues,
    trigger,
    disabled,
    formProps,
    datagridFormState,
  } = props;
  const dispatch = useDispatch();
  const [getData, { isLoading }] = useGetDataMutation({
    fixedCacheKey: fieldProps.key,
  });
  const { isDialogForm } = useFormContext();

  const { tags, label, properties = {} } = fieldProps;
  const {
    url,
    methodPost,
    queryParamKeys,
    updateKeys = "",
    autoFetchOnLoad,
    resultValue = "Y",
    openFormDialogOnFailure,
    verifySuccessMessage,
  } = properties;

  const verifyToggleBtnKey = `${fieldProps.key}Verified`;

  const openFormDialog = () => {
    dispatch(
      updateDialogActionPanel({
        fieldProps,
        show: true,
        type: "userInfoEdit",
        applicationData: formProps?.applicationData,
      })
    );
  };

  const onBtnClick = async (isAutoClick) => {
    const requiredFields = datagridFormState
      ? requireFieldsInfo.requiredFields[datagridFormState.datagridKey]
      : requireFieldsInfo.requiredFields;

    const verifyToggleBtnIsPresent = verifyToggleBtnKey in requiredFields;

    if (tags && tags.length && url) {
      const isValid = await trigger(tags);
      const shouldValidate = { shouldValidate: true };
      if (isValid) {
        const searchUri = constructSearchUrl(url, getValues());
        const params = getParams({ queryParamKeys }, getValues());
        const payload = { url: searchUri };
        if (methodPost) {
          payload.body = { data: {} };
          Object.entries(params).forEach(([key, value]) => {
            if (key.startsWith("data."))
              payload.body.data[key.split("data.")[1]] = value;
            else payload.body[key] = value;
          });
        } else payload.params = params;
        try {
          const response = await getData(payload).unwrap();
          if (response.isValid?.toString() === resultValue?.toString()) {
            updateKeys.split(",").forEach((key) => {
              let sourceKey = key;
              if (properties[sourceKey])
                sourceKey = properties[sourceKey].substring(1);
              if (sourceKey in response) setValue(key, response[sourceKey]);
            });
            if (verifyToggleBtnIsPresent)
              setValue(verifyToggleBtnKey, true, shouldValidate);
            if (verifySuccessMessage) {
              const msg = response.message || verifySuccessMessage;
              dispatch(showNotification({ type: "success", msg, show: true }));
            }
          } else {
            if (verifyToggleBtnIsPresent)
              setValue(verifyToggleBtnKey, false, shouldValidate);
            if (openFormDialogOnFailure && !isAutoClick) openFormDialog();
            else
              notifyError(dispatch, showNotification, {
                data: { message: response.message },
              });
          }
        } catch (err) {
          if (verifyToggleBtnIsPresent)
            setValue(verifyToggleBtnKey, false, shouldValidate);
          notifyError(dispatch, showNotification, err);
        }
      } else {
        const msg = "Please fill the required fields.";
        dispatch(showNotification({ type: "warning", msg, show: true }));
      }
    }
  };

  useEffect(() => {
    const hasVerified = getValues(verifyToggleBtnKey);
    if (hasVerified && autoFetchOnLoad && !isDialogForm) onBtnClick(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LoadingButton
      variant="contained"
      onClick={() => onBtnClick()}
      startIcon={<Check />}
      disabled={disabled}
      loading={isLoading}
      loadingPosition="start"
    >
      {label}
    </LoadingButton>
  );
}
VerifyAndUpdateBtn.propTypes = {
  fieldProps: PropTypes.shape({
    label: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  trigger: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

export default VerifyAndUpdateBtn;
